/**
 * Office-setting catalogue (D-123 – D-126, D-128, D-039, D-066, D-074, D-118,
 * D-207). Every office setting is a typed key: the API validates the value,
 * requires the card's permission token plus the caller's own PIN (D-123), and
 * writes an audit record with before / after (D-134). Personal preferences
 * (`USER_PREFERENCE_KEYS`) are per user, no PIN, not audited (D-123).
 *
 * Values are JSON. Amounts and percentages are decimal strings (ADR-02 §2.9).
 */
import { z } from "zod";
import { voucherFormatsSchema } from "./documents.js";

export type SettingsCard =
  | "identity" // هوية المكتب والعمل (company_settings)
  | "notifications" // settings_notifications
  | "security" // security_codes (the Security card, D-133)
  | "sub_safes" // settings_sub_safes
  | "digital_profits" // digital_profits_management
  | "currencies" // settings_currencies
  | "maintenance"; // system_maintenance

export const CARD_PERMISSION: Readonly<Record<SettingsCard, string>> = {
  identity: "company_settings",
  notifications: "settings_notifications",
  security: "security_codes",
  sub_safes: "settings_sub_safes",
  digital_profits: "digital_profits_management",
  currencies: "settings_currencies",
  maintenance: "system_maintenance",
};

/** D-087 alphabet for transfer codes (no 0/O, 1/I confusion), used by D-125's prefix. */
export const TRANSFER_CODE_ALPHABET = "ABCDEFGHJKLMNPQRSTUVWXYZ";
/** [C23] D-087 body of a generated code: 8 characters from letters and digits without look-alikes (no 0/O, 1/I). */
export const TRANSFER_CODE_BODY_ALPHABET = "ABCDEFGHJKLMNPQRSTUVWXYZ23456789";
const prefixRe = new RegExp(`^[${TRANSFER_CODE_ALPHABET}]{2,4}$`);

const decimalString = z.string().regex(/^-?\d+(\.\d+)?$/, "DECIMAL_STRING");
const nonNegPct = decimalString.refine((s) => Number(s) >= 0 && Number(s) <= 100, "PERCENT_RANGE");

export interface SettingDef<T = unknown> {
  readonly key: string;
  readonly card: SettingsCard;
  readonly schema: z.ZodType<T>;
  readonly defaultValue: T;
  readonly decision: string;
  /** Super Manager only, regardless of the card token (D-125 prefix, D-036/D-207 2FA policy). */
  readonly superOnly?: boolean;
}

const def = <T>(d: SettingDef<T>): SettingDef<T> => d;

export const OFFICE_SETTINGS = [
  // --- identity card (D-125) ---
  // default = the office name given at provisioning (the API seeds it); empty is allowed afterwards only by an explicit change
  def({ key: "identity.company_name", card: "identity", schema: z.string().trim().max(120), defaultValue: "", decision: "D-125" }),
  def({ key: "identity.phone1", card: "identity", schema: z.string().trim().max(32), defaultValue: "", decision: "D-125" }),
  def({ key: "identity.phone2", card: "identity", schema: z.string().trim().max(32), defaultValue: "", decision: "D-125" }),
  def({ key: "identity.address", card: "identity", schema: z.string().trim().max(240), defaultValue: "", decision: "D-125" }),
  def({ key: "identity.manager_name", card: "identity", schema: z.string().trim().max(120), defaultValue: "", decision: "D-125" }),
  def({ key: "identity.accountant_name", card: "identity", schema: z.string().trim().max(120), defaultValue: "", decision: "D-125" }),
  /** [C35] D-148: false = hide profit and commission on every customer-facing document, voucher and share text (internal reports unaffected). */
  def({ key: "identity.show_profit_in_prints", card: "identity", schema: z.boolean(), defaultValue: true, decision: "D-125 / D-148" }),
  /** [C35] D-149: the print format of each single-operation voucher (switchable per copy in the print dialog). */
  def({ key: "identity.voucher_formats", card: "identity", schema: voucherFormatsSchema, defaultValue: { customer_entry: "a4", transfer_voucher: "a4", installment_receipt: "a4", sales_invoice: "a4" }, decision: "D-149" }),
  /** [C35] O-4: the office logo printed in every header — a PNG / JPEG / WebP data URI of at most 300 KB ("" = none; old 2 MB file, App.tsx:15255-15265). */
  def({ key: "identity.logo", card: "identity", schema: z.union([z.literal(""), z.string().max(410_000).regex(/^data:image\/(png|jpeg|webp);base64,[A-Za-z0-9+/]+={0,2}$/, "LOGO_FORMAT")]), defaultValue: "", decision: "O-4 / D-052" }),
  def({ key: "identity.transfer_code_prefix", card: "identity", schema: z.string().regex(prefixRe, "PREFIX_FORMAT"), defaultValue: "FLS", decision: "D-125", superOnly: true }),
  def({ key: "identity.digital_commission_default_pct", card: "identity", schema: nonNegPct, defaultValue: "0.1", decision: "D-125 / D-074" }),

  // --- security card (D-133, D-039, D-168, D-207, D-040) ---
  def({ key: "security.idle_timeout_minutes", card: "security", schema: z.number().int().min(1).max(60), defaultValue: 15, decision: "D-039 / D-168" }),
  def({ key: "security.two_factor_required_for_all", card: "security", schema: z.boolean(), defaultValue: true, decision: "D-207", superOnly: true }),
  def({ key: "security.approved_devices_only", card: "security", schema: z.boolean(), defaultValue: false, decision: "D-040 (enforced after G1p)", superOnly: true }),
  def({ key: "security.force_second_party_approval", card: "security", schema: z.boolean(), defaultValue: false, decision: "D-035 / D-119" }),

  // --- notifications card (D-124, D-138) ---
  def({ key: "notifications.master", card: "notifications", schema: z.boolean(), defaultValue: true, decision: "D-124" }),
  def({
    key: "notifications.categories",
    card: "notifications",
    schema: z.object({
      debts_and_trusts: z.boolean(),
      withdraw_deposit: z.boolean(),
      usd_trading: z.boolean(),
      products: z.boolean(),
      delete_edit: z.boolean(),
      cash_transfers: z.boolean(),
    }),
    defaultValue: { debts_and_trusts: true, withdraw_deposit: true, usd_trading: true, products: true, delete_edit: true, cash_transfers: true },
    decision: "D-124",
  }),
  def({ key: "notifications.stagnant_debt_days", card: "notifications", schema: z.number().int().min(1).max(3650), defaultValue: 30, decision: "D-124 (≥ 1)" }),

  // --- sub-safes card (D-118) ---
  def({ key: "sub_safes.active", card: "sub_safes", schema: z.boolean(), defaultValue: false, decision: "D-118 (switched by the custody_activate / custody_deactivate operations; mirrored here for the engine context)" }),

  // --- currencies card (D-066) ---
  def({ key: "currencies.usd_dealing_hidden", card: "currencies", schema: z.boolean(), defaultValue: false, decision: "D-066", superOnly: true }),
  /** The single Currency Vault switch (old `companySettings.showCurrencyVault`, D04 §5 "USD / currency vault cards"): the sidebar section and the Wallets card follow it. */
  def({ key: "currencies.show_vault", card: "currencies", schema: z.boolean(), defaultValue: true, decision: "D-069 (C19; Super Manager only, C21 D-244)", superOnly: true }),

  // --- [C33] add-on modules (D-023, D-116): enforced by the API; off hides the screens, the records stay ---
  def({ key: "modules.installments", card: "maintenance", schema: z.boolean(), defaultValue: true, decision: "D-116 (C33 D-309)", superOnly: true }),
  def({ key: "modules.store", card: "maintenance", schema: z.boolean(), defaultValue: true, decision: "D-116 (C33 D-309)", superOnly: true }),

  // --- maintenance card ---
  def({ key: "maintenance.show_rates_button", card: "maintenance", schema: z.boolean(), defaultValue: true, decision: "D-124 (module / visibility switches)" }),
  def({ key: "maintenance.show_matching_button", card: "maintenance", schema: z.boolean(), defaultValue: true, decision: "D-124" }),
  def({ key: "maintenance.show_upgrades_button", card: "maintenance", schema: z.boolean(), defaultValue: true, decision: "D-124" }),
] as const satisfies readonly SettingDef[];

export const OFFICE_SETTING_BY_KEY: ReadonlyMap<string, SettingDef> = new Map(OFFICE_SETTINGS.map((s) => [s.key, s as SettingDef]));

export function officeSettingDefaults(): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const s of OFFICE_SETTINGS) out[s.key] = s.defaultValue;
  return out;
}

/** Per-user preferences (D-123): theme, language, layout. Validated, not audited, no PIN. */
export const USER_PREFERENCES = {
  "ui.theme": z.enum(["light", "dark"]),
  "ui.language": z.enum(["ar", "en"]),
  "ui.display_scale": z.number().int().min(85).max(130), // [C30 review] the old Theme Studio slider: 85 – 130 step 5 (ThemeStudioModal.tsx:219-221)
  "ui.icon_size": z.enum(["sm", "md", "lg", "xl"]), // [C30] the old Theme Studio offers xl (ThemeStudioModal.tsx:273)
  "ui.card_size": z.enum(["sm", "md", "lg"]),
  "ui.accent": z.string().regex(/^#[0-9a-fA-F]{6}$/),
  "ui.theme_studio": z.record(z.string(), z.unknown()),
  "ui.icons": z.record(z.string(), z.string()),
  "ui.sidebar_order": z.array(z.string()).max(64),
  "ui.settings_order": z.array(z.string()).max(64),
  "ui.dashboard_order": z.array(z.string()).max(64),
  "ui.dashboard_hidden": z.array(z.string()).max(64),
  "login.remembered_username": z.string().max(64), // D-039: username only, never the password
} as const;

export type UserPreferenceKey = keyof typeof USER_PREFERENCES;
export const USER_PREFERENCE_KEYS = new Set(Object.keys(USER_PREFERENCES));
